import { css } from "@emotion/react";
import { Suspense, useCallback, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import { Outlet, useLoaderData, useLocation, useNavigate } from "react-router";
import { Button } from "react-aria-components";

import {
  Counter,
  Flex,
  Icon,
  Icons,
  IconButton,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  PopoverArrow,
  Text,
} from "@phoenix/components";
import { SelectChevronUpDownIcon } from "@phoenix/components/core/icon";
import { AgentChatPanel } from "@phoenix/components/agent";
import {
  Brand,
  NavBreadcrumb,
  NavLink,
  NavTitle,
  SideNavbar,
  SideNavToggleButton,
  TopNavbar,
} from "@phoenix/components/nav";
import { useAgentContext } from "@phoenix/contexts/AgentContext";
import { useFeatureFlag } from "@phoenix/contexts/FeatureFlagsContext";
import { useFunctionality } from "@phoenix/contexts/FunctionalityContext";
import {
  usePreferencesContext,
  useTheme,
  useViewer,
} from "@phoenix/contexts";
import { prependBasename } from "@phoenix/utils/routingUtils";

import type { LayoutLoaderData } from "./layoutLoader";

const layoutCSS = css`
  display: flex;
  direction: row;
  height: 100vh;
  overflow: hidden;
`;

const mainViewCSS = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  overflow: hidden;
`;
const contentCSS = css`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  border-left: 1px solid var(--ac-premium-glass-border);
  border-top: 1px solid var(--ac-premium-glass-border);
  border-radius: var(--ac-premium-card-radius) 0 0 0;
  /* Fill the background of the content */
  box-shadow: var(--ac-premium-shadow-elevated);
  background-color: var(--global-background-color-default);
`;

const sideLinksCSS = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  margin: 0;
  list-style: none;

  /* Hide scrollbar visually while preserving scrolling */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge legacy */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Edge */
  }
`;

const brandContainerCSS = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 12px;
  padding: 0 4px;
  flex-shrink: 0;

  nav[data-expanded="false"] & {
    padding: 0;
    justify-content: center;
  }
`;

const categoryHeaderCSS = css`
  height: 28px;
  min-height: 28px;
  padding: 0 8px;
  margin: 6px 0 4px 0;
  font-size: 13.5px;
  font-weight: 650;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #000000;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  opacity: 1;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  transition: all 0.15s ease;

  .theme--dark & {
    color: #ffffff;
  }

  &:hover {
    color: #00685f;
    .theme--dark & {
      color: #14bab6;
    }
  }

  .category-label-wrap {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 7px;
    color: inherit;

    .material-symbols-outlined {
      font-size: 16px;
      color: inherit;
      font-weight: 600;
    }

    .icon-wrap {
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      color: inherit;

      svg {
        width: 15px;
        height: 15px;
        stroke-width: 2.2;
      }
    }
  }

  .chevron {
    color: inherit;
    opacity: 1;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    svg {
      width: 13px;
      height: 13px;
      stroke-width: 2.2;
    }
  }
`;

const categoryGroupCSS = css`
  display: flex;
  flex-direction: column;
  width: 100%;

  &:not(:first-of-type) {
    margin-top: 14px;
  }
`;

const submenuListCSS = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  margin: 0;
  list-style: none;

  .text {
    font-size: 13px !important;
    line-height: 18px !important;
  }
`;

const userProfileContainerCSS = css`
  flex-shrink: 0;
  margin-top: auto;
  width: 100%;
  padding: 8px 4px 4px 4px;
  border-top: 1px solid var(--ac-premium-glass-border, #e2e8f0);
  box-sizing: border-box;

  nav[data-expanded="false"] & {
    padding: 8px 0 4px 0;
    display: flex;
    justify-content: center;
  }
`;

const userCardButtonCSS = css`
  width: 100%;
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: var(--global-rounding-medium, 8px);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-align: start;
  color: inherit;
  transition: background-color 0.15s ease-in-out;
  outline: none;

  nav[data-expanded="false"] & {
    padding: 4px;
    justify-content: center;
    width: auto;
  }

  &:hover {
    background-color: var(--global-color-gray-100);
  }

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #e0f2fe;
    color: #00685f;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    flex-shrink: 0;
  }

  .user-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1;
  }

  .name {
    font-size: 13px;
    font-weight: 600;
    color: var(--global-text-color-900);
    margin: 0;
    line-height: 1.2;
  }

  .role {
    font-size: 11px;
    font-weight: 400;
    color: var(--global-text-color-500, #64748b);
    margin: 1px 0 0 0;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-card-chevron {
    color: #94a3b8;
    margin-left: auto;
    display: flex;
    align-items: center;
  }
`;

const logoutMenuItemCSS = css`
  color: var(--global-color-danger, #ba1a1a) !important;
  .icon-wrap {
    color: var(--global-color-danger, #ba1a1a) !important;
  }
`;

export function Layout() {
  const isAgentsEnabled = useFeatureFlag("agents");
  const isAgentPanelOpen = useAgentContext((state) => state.isOpen);
  const activePanelLocation = useAgentContext(
    (state) => state.activePanelLocation
  );
  const shouldShowDockedAgentPanel =
    isAgentsEnabled && isAgentPanelOpen && activePanelLocation === "docked";
  const panelIds = shouldShowDockedAgentPanel
    ? ["layout-content", "agent-chat"]
    : ["layout-content"];
  void panelIds;

  return (
    <div css={layoutCSS} data-testid="layout">
      <NavTitle />
      <SideNav />
      <div css={mainViewCSS}>
        <TopNavbar>
          <SideNavToggleButton />
          <NavBreadcrumb />
        </TopNavbar>
        <PanelGroup id="layout-panels" direction="horizontal">
          <Panel id="layout-content">
            <div data-testid="content" css={contentCSS}>
              <Suspense fallback={<Loading />}>
                <Outlet />
              </Suspense>
            </div>
          </Panel>
          {shouldShowDockedAgentPanel ? <AgentChatPanel /> : null}
        </PanelGroup>
      </div>
    </div>
  );
}



function SideNav() {
  const { isSideNavExpanded, setIsSideNavExpanded } = usePreferencesContext(
    (state) => ({
      isSideNavExpanded: state.isSideNavExpanded,
      setIsSideNavExpanded: state.setIsSideNavExpanded,
    })
  );
  const loaderData = useLoaderData<LayoutLoaderData>();
  const { authenticationEnabled } = useFunctionality();
  const { theme, setThemeMode } = useTheme();
  const { viewer } = useViewer();
  const navigate = useNavigate();

  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isSideNavExpanded || isHovered;

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    observability: true,
    sandbox: true,
    security: true,
    dataLogic: true,
    devCore: true,
  });

  const toggleCategory = useCallback((key: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const rawUsername = viewer?.username || "Kalaiyarasi";
  const username = rawUsername.charAt(0).toUpperCase() + rawUsername.slice(1);
  const roleName = viewer?.role?.name === "ADMIN" ? "Admin" : viewer?.role?.name === "VIEWER" ? "Viewer" : "Member";
  const initialLetter = username.charAt(0).toUpperCase();

  const toggleTheme = useCallback(() => {
    const nextMode = theme === "dark" ? "light" : "dark";
    setThemeMode(nextMode);
  }, [theme, setThemeMode]);

  const onLogout = useCallback(() => {
    window.location.replace(prependBasename("/auth/logout"));
  }, []);

  const handleUserMenuAction = useCallback((id: string | number) => {
    if (id === "settings") {
      navigate("/settings/general");
    } else if (id === "profile") {
      navigate("/profile");
    } else if (id === "logout") {
      onLogout();
    } else if (id === "theme-toggle") {
      toggleTheme();
    }
  }, [navigate, onLogout, toggleTheme]);

  return (
    <SideNavbar
      isExpanded={isExpanded}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div css={brandContainerCSS}>
        <Brand />
        {isExpanded && (
          <IconButton
            size="S"
            onPress={() => setIsSideNavExpanded(!isSideNavExpanded)}
            aria-label={isSideNavExpanded ? "Collapse side" : "Expand side"}
            style={{ color: "#8598ad" }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </IconButton>
        )}
      </div>
      <ul css={sideLinksCSS}>
          {/* OBSERVABILITY */}
          <div className="category-group" css={categoryGroupCSS}>
            {isExpanded && (
              <div css={categoryHeaderCSS} onClick={() => toggleCategory("observability")}>
                <div className="category-label-wrap">
                  <span className="material-symbols-outlined">visibility</span>
                  <span>Observability</span>
                </div>
                <span className="chevron material-symbols-outlined" style={{ fontSize: "18px", color: "inherit", fontWeight: 600 }}>{expandedCategories.observability ? "expand_less" : "expand_more"}</span>
              </div>
            )}
            {(!isExpanded || expandedCategories.observability) && (
              <ul className="submenu-list" css={submenuListCSS}>
                <li key="dashboard">
                  <NavLink
                    to="/dashboard"
                    text="Dashboard"
                    leadingVisual={<span className="material-symbols-outlined">grid_view</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="projects">
                  <NavLink
                    to="/projects"
                    text="Projects"
                    leadingVisual={<span className="material-symbols-outlined">folder</span>}
                    trailingVisual={
                      loaderData?.projectCount != null ? (
                        <Counter variant="quiet">{loaderData.projectCount}</Counter>
                      ) : undefined
                    }
                    isExpanded={isExpanded}
                    isActive={(isActive, location) =>
                      isActive && !location.pathname.includes("/dashboard")
                    }
                  />
                </li>
                <li key="evaluation-activity">
                  <NavLink
                    to="/templates/activity"
                    text="Evaluation Activity"
                    leadingVisual={<span className="material-symbols-outlined">show_chart</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="system-health">
                  <NavLink
                    to="/system-health"
                    text="System Health"
                    leadingVisual={<span className="material-symbols-outlined">monitor_heart</span>}
                    isExpanded={isExpanded}
                  />
                </li>
              </ul>
            )}
          </div>

          {/* DEVELOPMENT SANDBOX */}
          <div className="category-group" css={categoryGroupCSS}>
            {isExpanded && (
              <div css={categoryHeaderCSS} onClick={() => toggleCategory("sandbox")}>
                <div className="category-label-wrap">
                  <span className="material-symbols-outlined">science</span>
                  <span>Development Sandbox</span>
                </div>
                <span className="chevron material-symbols-outlined" style={{ fontSize: "18px", color: "inherit", fontWeight: 600 }}>{expandedCategories.sandbox ? "expand_less" : "expand_more"}</span>
              </div>
            )}
            {(!isExpanded || expandedCategories.sandbox) && (
              <ul className="submenu-list" css={submenuListCSS}>
                <li key="playground">
                  <NavLink
                    to="/playground"
                    text="Playground"
                    leadingVisual={<span className="material-symbols-outlined">play_circle</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="prompts">
                  <NavLink
                    to="/prompts"
                    text="Prompts"
                    leadingVisual={<span className="material-symbols-outlined">chat</span>}
                    trailingVisual={
                      loaderData?.promptCount != null ? (
                        <Counter variant="quiet">{loaderData.promptCount}</Counter>
                      ) : undefined
                    }
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="evaluators">
                  <NavLink
                    to="/evaluators"
                    text="Evaluators"
                    leadingVisual={<span className="material-symbols-outlined">fact_check</span>}
                    trailingVisual={
                      loaderData?.evaluatorCount != null ? (
                        <Counter variant="quiet">{loaderData.evaluatorCount}</Counter>
                      ) : undefined
                    }
                    isExpanded={isExpanded}
                  />
                </li>
              </ul>
            )}
          </div>

          {/* SECURITY */}
          <div className="category-group" css={categoryGroupCSS}>
            {isExpanded && (
              <div css={categoryHeaderCSS} onClick={() => toggleCategory("security")}>
                <div className="category-label-wrap">
                  <span className="material-symbols-outlined">shield</span>
                  <span>Security</span>
                </div>
                <span className="chevron material-symbols-outlined" style={{ fontSize: "18px", color: "inherit", fontWeight: 600 }}>{expandedCategories.security ? "expand_less" : "expand_more"}</span>
              </div>
            )}
            {(!isExpanded || expandedCategories.security) && (
              <ul className="submenu-list" css={submenuListCSS}>
                <li key="red-teaming">
                  <NavLink
                    to="/red-teaming"
                    text="Red Teaming"
                    leadingVisual={<span className="material-symbols-outlined">shield</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="scan-history">
                  <NavLink
                    to="/security/scan-history"
                    text="Scan History"
                    leadingVisual={<span className="material-symbols-outlined">history</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="reports">
                  <NavLink
                    to="/security/reports"
                    text="Reports"
                    leadingVisual={<span className="material-symbols-outlined">bar_chart</span>}
                    isExpanded={isExpanded}
                  />
                </li>
              </ul>
            )}
          </div>

          {/* DATA & LOGIC */}
          <div className="category-group" css={categoryGroupCSS}>
            {isExpanded && (
              <div css={categoryHeaderCSS} onClick={() => toggleCategory("dataLogic")}>
                <div className="category-label-wrap">
                  <span className="material-symbols-outlined">database</span>
                  <span>Data & Logic</span>
                </div>
                <span className="chevron material-symbols-outlined" style={{ fontSize: "18px", color: "inherit", fontWeight: 600 }}>{expandedCategories.dataLogic ? "expand_less" : "expand_more"}</span>
              </div>
            )}
            {(!isExpanded || expandedCategories.dataLogic) && (
              <ul className="submenu-list" css={submenuListCSS}>
                <li key="datasets">
                  <NavLink
                    to="/datasets"
                    text="Datasets & Experiments"
                    leadingVisual={<span className="material-symbols-outlined">grid_on</span>}
                    trailingVisual={
                      loaderData?.datasetCount != null ? (
                        <Counter variant="quiet">{loaderData.datasetCount}</Counter>
                      ) : undefined
                    }
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="templates">
                  <NavLink
                    to="/templates"
                    text="Templates"
                    leadingVisual={<span className="material-symbols-outlined">description</span>}
                    isExpanded={isExpanded}
                    isActive={(isActive, location) => {
                      return location.pathname.startsWith("/templates") && !location.pathname.includes("/activity");
                    }}
                  />
                </li>
              </ul>
            )}
          </div>

          {/* DEVELOPER CORE */}
          <div className="category-group" css={categoryGroupCSS}>
            {isExpanded && (
              <div css={categoryHeaderCSS} onClick={() => toggleCategory("devCore")}>
                <div className="category-label-wrap">
                  <span className="material-symbols-outlined">code</span>
                  <span>Developer Core</span>
                </div>
                <span className="chevron material-symbols-outlined" style={{ fontSize: "18px", color: "inherit", fontWeight: 600 }}>{expandedCategories.devCore ? "expand_less" : "expand_more"}</span>
              </div>
            )}
            {(!isExpanded || expandedCategories.devCore) && (
              <ul className="submenu-list" css={submenuListCSS}>
                <li key="rest-api">
                  <NavLink
                    to="/apis/rest"
                    text="REST API"
                    leadingVisual={<span className="material-symbols-outlined">api</span>}
                    isExpanded={isExpanded}
                  />
                </li>
                <li key="graphql">
                  <NavLink
                    to="/apis/graphql"
                    text="GraphQL"
                    leadingVisual={<span className="material-symbols-outlined">share</span>}
                    isExpanded={isExpanded}
                  />
                </li>
              </ul>
            )}
          </div>
        </ul>
        <div css={userProfileContainerCSS}>
          <MenuTrigger>
            <Button css={userCardButtonCSS} className="button--reset">
              <div className="avatar">{initialLetter}</div>
              {isExpanded && (
                <>
                  <div className="user-info">
                    <p className="name">{username}</p>
                    <p className="role">{viewer?.email}</p>
                  </div>
                  <div className="user-card-chevron">
                    <SelectChevronUpDownIcon />
                  </div>
                </>
              )}
            </Button>
            <Popover placement="right bottom" offset={10}>
              <PopoverArrow />
              <Menu aria-label="User actions" onAction={handleUserMenuAction}>
                <MenuItem id="settings">
                  <Flex direction="row" gap="size-100" alignItems="center">
                    <Icon svg={<Icons.SettingsOutline />} />
                    <Text>Settings</Text>
                  </Flex>
                </MenuItem>
                <MenuItem id="theme-toggle">
                  <Flex direction="row" gap="size-100" alignItems="center">
                    <Icon svg={theme === "dark" ? <Icons.SunOutline /> : <Icons.MoonOutline />} />
                    <Text>{theme === "dark" ? "Light" : "Dark"}</Text>
                  </Flex>
                </MenuItem>
                <MenuItem id="profile">
                  <Flex direction="row" gap="size-100" alignItems="center">
                    <Icon svg={<Icons.PersonOutline />} />
                    <Text>Profile</Text>
                  </Flex>
                </MenuItem>
                {authenticationEnabled && (
                  <MenuItem id="logout" css={logoutMenuItemCSS}>
                    <Flex direction="row" gap="size-100" alignItems="center">
                      <Icon svg={<Icons.LogOut />} />
                      <Text>Log Out</Text>
                    </Flex>
                  </MenuItem>
                )}
              </Menu>
            </Popover>
          </MenuTrigger>
        </div>
    </SideNavbar>
  );
}
